import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPublishedStations } from '../../../api/fetch.js';

/**
 * usePublishedStations — thin data hook around the existing public
 * `GET /api/stations?published=true&active=true` endpoint.
 *
 * Behaviour:
 *   - Fetches on mount (a fresh request every time the page is
 *     mounted) so charger availability is never stale across
 *     navigation.
 *   - Concurrent calls (React StrictMode double-invoke, or a rapid
 *     retry click) share a single in-flight promise. The reference
 *     is cleared as soon as the request settles, so the next mount
 *     or retry starts a brand new request.
 *   - No result cache. `refetch()` always initiates a new fetch.
 *   - No polling.
 */

let inFlight = null;

function fetchOnce() {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      return await fetchPublishedStations();
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export default function usePublishedStations() {
  const [state, setState] = useState({ stations: null, loading: true, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const cancelRef = useRef({ current: false });

  useEffect(() => {
    const gate = { current: false };
    cancelRef.current = gate;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    (async () => {
      try {
        const resp = await fetchOnce();
        if (gate.current) return;
        setState({
          stations: Array.isArray(resp?.stations) ? resp.stations : [],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (gate.current) return;
        setState({ stations: null, loading: false, error: err });
      }
    })();
    return () => {
      gate.current = true;
    };
  }, [reloadKey]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  return { ...state, refetch };
}
